import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../components/common/GlobalElements/PageHeader';
import Header from '../components/common/Header';
import { getCurrentTimeHomePage } from '../components/common/Helper';
import SideBar from '../components/common/SideBar';
import { Button } from '../components/library/Button';
import { TabButton } from '../components/library/TabButton';
import * as Cards from '../components/profile/index';
import StudentIdentityCard from '../components/profile/ProfilePictureUpload/StudentIdentityCard';
import { showPhotoEnrollmentDialog } from '../components/profile/ProfilePictureUpload/utils';
import { tokens } from '../public/tokens';
import { GetStatementStatus, getStudentDetails } from '../services/ProfileService';
import { setUser } from '../store/actions';
import { BASE_URL, UPLOAD_ROOT_PATH } from '../utils/Constants';
import { download } from '../utils/FileDownload';
import styled from 'styled-components';
import { XL_BREAKPOINT } from '../components/library/breakpoints';

/**
 * The identity card and the actions below it read as one padded block, so they
 * share the same horizontal inset. Neither pads the edge they meet on - the
 * card's own InfoList already carries the 20px gap down to the first button.
 */
const identityCardStyle = {
    padding: `${tokens.globals['spacing-md']} ${tokens.globals['spacing-md']} 0`,
};

const sidebarActionsStyle = {
    padding: `0 ${tokens.globals['spacing-md']} ${tokens.globals['spacing-md']}`,
};

const detailActionsStyle = {
    padding: '0 15px 15px',
};

const ProfileWrapper = styled.div({
    display: 'flex',
    width: '100%',
});

const ProfileLeft = styled.div({
    maxWidth: '300px',
    width: '300px',
    position: 'fixed',
    height: '100%',
    maxHeight: 'calc(100vh - 48px)',
    background: tokens.colors.surfaces['container-background'],
    borderRight: `1px solid ${tokens.colors.surfaces['border-divider']}`,
    zIndex: 10,

    [`@media (max-width: ${XL_BREAKPOINT})`]: {
        maxWidth: '40px',
        width: '40px',
        background: 'transparent',
        boxShadow: 'none',
        borderRight: 'none',
    },
});

const ProfileSidebarContent = styled.div(({ $isOpen }) => ({
    width: '100%',
    height: '100%',

    [`@media (max-width: ${XL_BREAKPOINT})`]: {
        display: $isOpen ? 'block' : 'none',
        position: 'fixed',
        top: '48px',
        left: '68px',
        width: '300px',
        height: 'calc(100vh - 48px)',
        background: tokens.colors.surfaces['container-background'],
        borderRight: `1px solid ${tokens.colors.surfaces['border-divider']}`,
        zIndex: 10,
        overflowY: 'hidden',
    },

    '@media (max-width: 768px)': {
        left: 0,
    },
}));

const SidebarToggleButton = styled.button(({ $isOpen }) => ({
    display: 'none',

    [`@media (max-width: ${XL_BREAKPOINT})`]: {
        display: 'flex',
        position: 'relative',
        zIndex: 20,
        width: '36px',
        height: '46px',
        padding: 0,
        border: 0,
        background: 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',

        ...($isOpen && {
            position: 'fixed',
            top: '48px',
            left: '328px',
        }),
    },

    '@media (max-width: 768px)': {
        ...($isOpen && {
            left: '260px',
        }),
    },

    '@media (min-width: 475px) and (max-width: 567px)': {
        marginTop: '48px',

        ...($isOpen && {
            top: '96px',
            marginTop: 0,
        }),
    },
}));

const ProfileRight = styled.div({
    width: 'calc(100% - 300px)',
    marginLeft: '300px',
    padding: `calc(${tokens.globals['spacing-md']} + 4px)`,
    boxSizing: 'border-box',

    [`@media (max-width: ${XL_BREAKPOINT})`]: {
        width: 'calc(100% - 40px)',
        marginLeft: '40px',
        paddingLeft: 0,
    },

    '@media (min-width: 475px) and (max-width: 567px)': {
        paddingTop: '64px',
    },
});

const Tabs = [
    {
        first_name: 'My',
        last_name: 'Profile',
        name: 'Personal Information',
        component: 'PersonalInformation',
        class: 'fa-user',
        id: 1,
    },
    {
        first_name: 'Educational',
        last_name: 'Details',
        name: 'Educational Details',
        component: 'EducationalDetails',
        class: 'fa-graduation-cap',
        id: 2,
    },
    {
        first_name: 'Sponsor/Account',
        last_name: 'Payer',
        name: 'Sponsor/Account Payer',
        component: 'SponsorPayer',
        class: 'fa-piggy-bank',
        id: 3,
    },
    {
        first_name: 'Certificates',
        last_name: '',
        name: 'Certificates',
        component: 'Certificates',
        class: 'fa-file-alt',
        id: 4,
    },
    {
        first_name: 'Licenses',
        last_name: '',
        name: 'Licenses',
        component: 'Licenses',
        class: 'fa-address-card',
        id: 5,
    },
    {
        first_name: 'Account',
        last_name: 'Statements',
        name: 'Account Statements',
        component: 'AccountStatement',
        class: 'fa-file-invoice-dollar',
        id: 6,
    },
];

export default function ProfilePage() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    let brand;

    const [state, setState] = useState({
        currentTab: 1,
        currentComponent: 'PersonalInformation',
    });

    const [disableStatement, setDisableStatement] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    /**
     * Biostar saves the enrolled photo against the student itself, so the only
     * way to pick it up is to refetch the student once enrollment succeeds.
     */
    const refreshUserAfterPhotoUpdate = async () => {
        try {
            const res = await getStudentDetails();
            dispatch(setUser(res.data.user));
        } catch (error) {
            console.log('ProfilePage->refreshUserAfterPhotoUpdate->Error', error);
        }
    };

    if (localStorage.getItem('brandVariables') != null) {
        brand = JSON.parse(localStorage.getItem('brandVariables'));
    }

    useEffect(() => {
        if (user.netsuite_id && user.netsuite_id != '') {
            GetStatementStatus({ id: user.netsuite_id })
                .then((res) => {
                    if (res.status == 200) {
                        const respData = res.data.data;

                        if (respData?.BadDebt === true || respData?.HandedOver === true) {
                            setDisableStatement(true);
                        }
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, []);

    const handleSideBar = () => {
        setIsSidebarOpen((prev) => !prev);
    };

    const handleViewCard = () => {
        window.open(
            BASE_URL + '/' + user?.electronic_student_card.replace(`${UPLOAD_ROOT_PATH}/`, '') + '#zoom=75',
            '_blank',
        );
    };

    const handleTab = (tab) => {
        setState({ currentTab: tab.id, currentComponent: tab.component });
        setIsSidebarOpen(false);
    };

    const handleTabContent = (component, key) => {
        if (
            component == 'EducationalDetails' &&
            (brand?.domain_url.includes('learnfast') ||
                brand?.domain_url.includes('cadco') ||
                (user?.student_brand_url &&
                    (user?.student_brand_url.includes('learnfast') || user?.student_brand_url.includes('cadco'))))
        ) {
            return <></>;
        }

        const Card = Cards[component];

        return <Card key={key} />;
    };

    return (
        <>
            <Header />

            <div className="content-wrapper d-flex flex-wrap">
                <SideBar />

                <div className="sidebar-right">
                    <ProfileWrapper>
                        <ProfileLeft>
                            <SidebarToggleButton
                                type="button"
                                $isOpen={isSidebarOpen}
                                onClick={handleSideBar}
                                aria-expanded={isSidebarOpen}
                                aria-label={isSidebarOpen ? 'Close profile sidebar' : 'Open profile sidebar'}
                            >
                                <i className={`fal ${isSidebarOpen ? 'fa-times' : 'fa-bars'}`}></i>
                            </SidebarToggleButton>

                            <ProfileSidebarContent $isOpen={isSidebarOpen}>
                                <div style={identityCardStyle}>
                                    <StudentIdentityCard user={user} showStatus />
                                </div>

                                <div style={sidebarActionsStyle}>
                                    <Button
                                        $fullWidth
                                        className="mb-2"
                                        onClick={() =>
                                            showPhotoEnrollmentDialog({
                                                onPhotoUpdated: refreshUserAfterPhotoUpdate,
                                                hasPhoto: !!user?.photo,
                                            })
                                        }
                                    >
                                        Update Profile Picture
                                        <i className="fal fa-arrow-right"></i>
                                    </Button>

                                    {user?.electronic_student_card !== undefined &&
                                    user?.electronic_student_card !== '' ? (
                                        <>
                                            <Button
                                                $variant="dark"
                                                $fullWidth
                                                className="mb-2"
                                                onClick={() => handleViewCard()}
                                            >
                                                <i className="fal fa-id-card"></i> View Student Card
                                            </Button>

                                            <Button
                                                $variant="dark"
                                                $fullWidth
                                                onClick={() => {
                                                    const fileName = 'my_card.pdf';
                                                    const absolutePath =
                                                        BASE_URL +
                                                        '/' +
                                                        user?.electronic_student_card.replace(
                                                            `${UPLOAD_ROOT_PATH}/`,
                                                            '',
                                                        );

                                                    download(absolutePath, fileName);
                                                }}
                                            >
                                                <i className="fal fa-download"></i> Download Student Card
                                            </Button>
                                        </>
                                    ) : null}
                                </div>

                                <div style={detailActionsStyle}>
                                    <ul className="nav nav-pills flex-column">
                                        {Tabs.map((tab) => {
                                            if (
                                                (tab.id == 2 || tab.id == 6) &&
                                                (brand?.domain_url?.includes('learnfast') ||
                                                    brand?.domain_url?.includes('cadco') ||
                                                    (user?.student_brand_url &&
                                                        user?.student_brand_url?.includes('learnfast')) ||
                                                    (user?.student_brand_url &&
                                                        user?.student_brand_url?.includes('cadco')))
                                            ) {
                                                return null;
                                            }

                                            return (
                                                <li className="nav-item" title={tab.name} key={tab.id}>
                                                    {tab.id === 6 &&
                                                    (disableStatement === true || user?.status == 'On Hold') ? (
                                                        <a
                                                            className="nav-link disabled"
                                                            data-toggle="tab"
                                                            href={'#profile-tab-' + tab.id}
                                                        >
                                                            <i className={'fal ' + tab.class}></i>
                                                            {tab.name}
                                                        </a>
                                                    ) : (
                                                        <TabButton
                                                            $active={tab.id == state.currentTab}
                                                            $fullWidth
                                                            data-toggle="tab"
                                                            onClick={() => handleTab(tab)}
                                                        >
                                                            <i className={'fal ' + tab.class}></i> {tab.name}
                                                        </TabButton>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </ProfileSidebarContent>
                        </ProfileLeft>

                        <ProfileRight>
                            <div className="profile-header">
                                <PageHeader
                                    title={
                                        <>
                                            {Tabs[state.currentTab - 1]?.first_name}{' '}
                                            <span>{Tabs[state.currentTab - 1]?.last_name}</span>
                                        </>
                                    }
                                    right={<p className=""> {getCurrentTimeHomePage(Date().toLocaleString())} </p>}
                                />

                                <hr />

                                <div className="tab-content profile-tab-content">
                                    <div id={'profile-tab-' + state.currentTab} className="tab-pane active">
                                        {handleTabContent(state.currentComponent, state.currentTab)}
                                    </div>
                                </div>
                            </div>
                        </ProfileRight>
                    </ProfileWrapper>
                </div>
            </div>
        </>
    );
}
